import {
	Blend,
	ChartBar,
	CircleDot,
	Contrast,
	Crop,
	Droplets,
	Eye,
	EyeOff,
	FileCode,
	Gauge,
	Home,
	Image,
	Keyboard,
	Layers,
	Palette,
	Pipette,
	Ruler,
	SlidersVertical,
	Sparkles,
	SwatchBook,
	Wine,
} from "lucide-react"
import type { ComponentType } from "react"
import { Link, useLocation } from "react-router-dom"

type NavItem = {
	chord?: string
	icon: ComponentType<{ className?: string }>
	name: string
	path: string
}

type NavGroup = {
	items: NavItem[]
	label: string
}

const navGroups: NavGroup[] = [
	{
		items: [
			{ chord: "g h", icon: Home, name: "Home", path: "/" },
			{
				chord: "g l",
				icon: Layers,
				name: "Color Spaces",
				path: "/color-spaces",
			},
			{ chord: "g s", icon: SwatchBook, name: "Standards", path: "/standards" },
			{ chord: "g c", icon: Pipette, name: "Convert", path: "/convert" },
		],
		label: "Explore",
	},
	{
		items: [
			{ chord: "g p", icon: Palette, name: "Palettes", path: "/palettes" },
			{ chord: "g w", icon: CircleDot, name: "Harmony", path: "/harmony" },
			{
				chord: "g i",
				icon: Sparkles,
				name: "Interpolation",
				path: "/interpolation",
			},
			{ chord: "g d", icon: ChartBar, name: "Data Viz", path: "/data-viz" },
		],
		label: "Generate",
	},
	{
		items: [
			{ chord: "g j", icon: Gauge, name: "Adjustments", path: "/adjustments" },
			{ chord: "g b", icon: Blend, name: "Blend Modes", path: "/blend-modes" },
			{
				chord: "g m",
				icon: Wine,
				name: "Alpha Mixing",
				path: "/alpha-mixing",
			},
			{
				chord: "g e",
				icon: Droplets,
				name: "Analog Effects",
				path: "/analog-effects",
			},
			{
				chord: "g o",
				icon: SlidersVertical,
				name: "Color Correction",
				path: "/color-correction",
			},
			{
				chord: "g u",
				icon: Crop,
				name: "Gamut Mapping",
				path: "/gamut-mapping",
			},
		],
		label: "Adjust",
	},
	{
		items: [
			{
				chord: "g a",
				icon: Contrast,
				name: "Accessibility",
				path: "/accessibility",
			},
			{
				chord: "g v",
				icon: EyeOff,
				name: "Color Blindness",
				path: "/color-blindness",
			},
			{ chord: "g f", icon: Ruler, name: "Color Diff", path: "/color-diff" },
		],
		label: "Analyze",
	},
	{
		items: [
			{
				chord: "g x",
				icon: Image,
				name: "Image Extraction",
				path: "/image-extraction",
			},
			{
				chord: "g y",
				icon: FileCode,
				name: "CSS Playground",
				path: "/css-playground",
			},
		],
		label: "Extract",
	},
	{
		items: [{ chord: "g ?", icon: Keyboard, name: "Shortcuts", path: "/help" }],
		label: "Help",
	},
]

type NavigationProps = {
	collapsed: boolean
	isOpen: boolean
	onClose: () => void
}

export function Navigation({ collapsed, isOpen, onClose }: NavigationProps) {
	const location = useLocation()

	const sidebarWidth = collapsed ? "w-14" : "w-56"

	const sidebar = (
		<nav
			className={`${sidebarWidth} shrink-0 h-screen overflow-y-auto py-4 flex flex-col gap-4 transition-[width] duration-200 border-r border-subtle`}
			style={{ backgroundColor: "var(--bg-page)" }}
		>
			<a
				className={`flex items-center gap-2 px-3 hover:opacity-80 transition-opacity ${collapsed ? "justify-center" : ""}`}
				href="https://gitlab.com/open-kappa/nodejs/colors"
				rel="noopener noreferrer"
				target="_blank"
			>
				<Eye className="w-5 h-5 shrink-0" style={{ color: "var(--accent)" }} />
				{!collapsed && (
					<span className="font-semibold text-sm text-neutral-400 truncate">
						@open-kappa/colors
					</span>
				)}
			</a>

			{navGroups.map((group) => (
				<div key={group.label}>
					{!collapsed && (
						<div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-neutral-600">
							{group.label}
						</div>
					)}
					{group.items.map((item) => {
						const Icon = item.icon
						const isActive =
							item.path === "/"
								? location.pathname === "/"
								: location.pathname.startsWith(item.path)
						return (
							<Link
								className={`relative flex items-center gap-2.5 px-3 py-1.5 text-sm transition-colors ${
									collapsed ? "justify-center" : ""
								} ${
									isActive
										? "text-neutral-300 font-medium"
										: "text-neutral-500 hover:text-neutral-300"
								}`}
								key={item.path}
								onClick={onClose}
								title={collapsed ? item.name : undefined}
								to={item.path}
							>
								{/* Active indicator bar */}
								{isActive && (
									<div
										className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
										style={{ backgroundColor: "var(--accent)" }}
									/>
								)}
								<Icon className="w-4 h-4 shrink-0" />
								{!collapsed && (
									<>
										<span className="truncate flex-1">{item.name}</span>
										{item.chord && (
											<kbd className="ml-auto shrink-0 px-1 py-0.5 rounded bg-neutral-800 text-neutral-500 font-mono text-[10px] border border-neutral-700">
												{item.chord}
											</kbd>
										)}
									</>
								)}
							</Link>
						)
					})}
				</div>
			))}

			<div className="mt-auto border-t border-subtle pt-3 px-3">
				{!collapsed ? (
					<>
						<div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-neutral-600">
							About
						</div>
						<p className="text-[11px] leading-relaxed text-neutral-500">
							<a
								className="text-purple-400 hover:text-purple-300 transition-colors"
								href="https://gitlab.com/traveltoaiur/colors-showcase"
								rel="noopener noreferrer"
								target="_blank"
							>
								Source code
							</a>{" "}
							on GitLab
						</p>
					</>
				) : (
					<a
						className="flex justify-center text-purple-400 hover:text-purple-300 transition-colors"
						href="https://gitlab.com/traveltoaiur/colors-showcase"
						rel="noopener noreferrer"
						target="_blank"
						title="@open-kappa/colors"
					>
						<Eye className="w-4 h-4" />
					</a>
				)}
			</div>
		</nav>
	)

	return (
		<>
			{/* Desktop sidebar */}
			<div className="hidden md:block sticky top-0 h-screen">{sidebar}</div>

			{/* Mobile overlay */}
			{isOpen && (
				<div className="md:hidden fixed inset-0 z-50 flex">
					<button
						aria-label="Close navigation"
						className="absolute inset-0 bg-black/50"
						onClick={onClose}
						type="button"
					/>
					<div className="relative z-10">{sidebar}</div>
				</div>
			)}
		</>
	)
}
